export type ScoreWinner = 'Us' | 'Opponent' | 'Unknown';
export type ScoreCompletionStatus = 'Not Started' | 'In Progress' | 'Complete';
export type ScoreLineStatus = 'Completed' | 'Retired' | 'Default' | 'Double Default';

export type MatchLineScoreDraft = {
  positionId: string;
  positionCode: string;
  displayName: string;
  displayOrder: number;
  lineType: string;
  winner: ScoreWinner;
  status: ScoreLineStatus;
  completionStatus: ScoreCompletionStatus;
  sets: Array<{ us: number | null; opponent: number | null }>;
  retired: boolean;
  defaulted: boolean;
  walkover: boolean;
  set3IsMatchTiebreak?: boolean;
  matchTiebreakExactScoreKnown?: boolean;
  matchTiebreakUsPoints?: number | null;
  matchTiebreakOpponentPoints?: number | null;
  matchTiebreakWinner?: ScoreWinner;
  notes: string;
};

export type TeamScoreSummary = {
  ourWins: number;
  opponentWins: number;
  result: 'Win' | 'Loss' | 'Tie' | 'Unknown';
};

export function hasMatchLineScoreData(line: MatchLineScoreDraft): boolean {
  return line.winner !== 'Unknown'
    || line.status !== 'Completed'
    || line.sets.some((set) => set.us != null || set.opponent != null)
    || Boolean(line.notes.trim());
}

export function isMatchLineFinal(line: MatchLineScoreDraft): boolean {
  if (!hasMatchLineScoreData(line)) return false;
  return validateMatchLineScore(line) === null;
}

export function validateMatchLineScore(line: MatchLineScoreDraft): string | null {
  const label = line.positionCode || line.displayName || 'Line';
  const enteredSets = line.sets.filter((set) => set.us != null || set.opponent != null);
  if (enteredSets.some((set) => set.us == null || set.opponent == null)) {
    return `${label}: enter both Us and Opponent scores for each set.`;
  }
  if (line.status === 'Double Default') {
    return line.winner === 'Unknown' ? null : `${label}: Double Default cannot have a winner.`;
  }
  if (line.winner === 'Unknown') return `${label}: select Us or Opponent as the winner.`;
  if (line.status !== 'Completed') return null;
  const firstSet = line.sets[0];
  const secondSet = line.sets[1];
  const thirdSet = line.sets[2];
  if (!isEnteredSet(firstSet) || !isEnteredSet(secondSet)) {
    return `${label}: completed matches require scores for Set 1 and Set 2.`;
  }
  if (!isValidCompletedSet(firstSet)) return `${label}: Set 1 is not a valid completed set score.`;
  if (!isValidCompletedSet(secondSet)) return `${label}: Set 2 is not a valid completed set score.`;

  const firstWinner = setWinner(firstSet);
  const secondWinner = setWinner(secondSet);
  let scoreWinner: ScoreWinner;
  if (firstWinner === secondWinner) {
    if (isEnteredSet(thirdSet)) return `${label}: Set 3 must be blank when the same team wins Set 1 and Set 2.`;
    scoreWinner = firstWinner;
  } else {
    if (!isEnteredSet(thirdSet) || !isMatchTiebreakScore(thirdSet)) {
      return `${label}: when the first two sets are split, Set 3 must be entered as 1-0 or 0-1.`;
    }
    scoreWinner = setWinner(thirdSet);
    if (line.matchTiebreakExactScoreKnown) {
      const usPoints = line.matchTiebreakUsPoints;
      const opponentPoints = line.matchTiebreakOpponentPoints;
      if (usPoints == null || opponentPoints == null) return `${label}: enter both exact match-tiebreak point totals.`;
      const high = Math.max(usPoints, opponentPoints);
      const low = Math.min(usPoints, opponentPoints);
      if (high < 10 || high - low < 2) return `${label}: exact match-tiebreak score must be won by 2 and normally reaches at least 10.`;
      const exactWinner = usPoints > opponentPoints ? 'Us' : 'Opponent';
      if (exactWinner !== scoreWinner) return `${label}: exact match-tiebreak points do not match the 1-0/0-1 Set 3 winner.`;
    }
  }
  return scoreWinner === line.winner
    ? null
    : `${label}: ${scoreWinner} won more sets, but ${line.winner} is selected as the winner.`;
}

function isEnteredSet(set: { us: number | null; opponent: number | null } | undefined): set is { us: number; opponent: number } {
  return Boolean(set && set.us != null && set.opponent != null);
}

function isValidCompletedSet(set: { us: number; opponent: number }): boolean {
  const high = Math.max(set.us, set.opponent);
  const low = Math.min(set.us, set.opponent);
  return high >= 6 && (high - low >= 2 || (high === 7 && low === 6));
}

function isMatchTiebreakScore(set: { us: number; opponent: number }): boolean {
  return (set.us === 1 && set.opponent === 0) || (set.us === 0 && set.opponent === 1);
}

function setWinner(set: { us: number; opponent: number }): Exclude<ScoreWinner, 'Unknown'> {
  return set.us > set.opponent ? 'Us' : 'Opponent';
}

export function compactSetScores(line: Pick<MatchLineScoreDraft, 'sets'>, winnerFirst = false, winner: ScoreWinner = 'Unknown'): string[] {
  return line.sets
    .filter((set) => set.us != null && set.opponent != null)
    .map((set) => {
      const reverse = winnerFirst && winner === 'Opponent';
      return reverse ? `${set.opponent}-${set.us}` : `${set.us}-${set.opponent}`;
    });
}

export function calculateTeamScore(lines: Array<Pick<MatchLineScoreDraft, 'winner'>>): TeamScoreSummary {
  const ourWins = lines.filter((line) => line.winner === 'Us').length;
  const opponentWins = lines.filter((line) => line.winner === 'Opponent').length;
  const entered = ourWins + opponentWins;
  return {
    ourWins,
    opponentWins,
    result: entered === 0 ? 'Unknown' : ourWins > opponentWins ? 'Win' : ourWins < opponentWins ? 'Loss' : 'Tie',
  };
}

export function legacyResultValue(line: Pick<MatchLineScoreDraft, 'winner' | 'status' | 'retired' | 'defaulted' | 'walkover'>): string {
  if (line.status === 'Double Default') return 'Double Default';
  if (line.winner === 'Unknown') return line.defaulted ? 'Double Default' : 'Not Entered';
  if (line.status === 'Retired' || line.retired) return line.winner === 'Us' ? 'Retired Win' : 'Retired Loss';
  if (line.status === 'Default' || line.defaulted || line.walkover) return line.winner === 'Us' ? 'Default by Opponent' : 'Default by Us';
  return line.winner === 'Us' ? 'Win' : 'Loss';
}

export function legacyLineResult(line: Pick<MatchLineScoreDraft, 'winner' | 'status' | 'retired' | 'defaulted' | 'walkover'>): 'win' | 'loss' | 'tie' | 'defaulted' | 'unfinished' {
  const value = legacyResultValue(line);
  if (value === 'Win' || value === 'Retired Win' || value === 'Default by Opponent') return 'win';
  if (value === 'Loss' || value === 'Retired Loss') return 'loss';
  if (value === 'Double Default') return 'tie';
  if (value === 'Default by Us') return 'defaulted';
  return 'unfinished';
}

export function buildScoreConfirmation(lines: MatchLineScoreDraft[]): string {
  return [
    'These are the scores I got from my team. Does this look correct?',
    '',
    ...lines.map((line) => `${line.positionCode}: ${scoreText(line)} (${line.winner === 'Us' ? 'us' : line.winner === 'Opponent' ? 'you' : 'unknown'})`),
  ].join('\n');
}

export function buildUstaFormat(lines: MatchLineScoreDraft[], ourTeamName: string, opponentName: string): string {
  return lines.map((line) => {
    const winner = line.winner === 'Us' ? ourTeamName : line.winner === 'Opponent' ? opponentName : 'Unknown';
    const loser = line.winner === 'Us' ? opponentName : line.winner === 'Opponent' ? ourTeamName : 'Unknown';
    const scores = compactSetScores(line, true, line.winner).join(', ');
    return `${line.positionCode}: ${winner} def. ${loser}${scores ? ` ${scores}` : ''}${specialSuffix(line)}`;
  }).join('\n');
}

export function buildTeamResults(lines: MatchLineScoreDraft[], ourTeamName: string, opponentName: string): string {
  const summary = calculateTeamScore(lines);
  const headline = summary.result === 'Win'
    ? `${ourTeamName} defeated ${opponentName} ${summary.ourWins}-${summary.opponentWins}`
    : summary.result === 'Loss'
      ? `${opponentName} defeated ${ourTeamName} ${summary.opponentWins}-${summary.ourWins}`
      : `${ourTeamName} tied ${opponentName} ${summary.ourWins}-${summary.opponentWins}`;
  return [
    headline,
    '',
    ...lines.map((line) => `${line.positionCode}: ${line.winner === 'Us' ? 'Won' : line.winner === 'Opponent' ? 'Lost' : 'Unknown'} ${scoreText(line)}${specialSuffix(line)}`.trim()),
  ].join('\n');
}

function scoreText(line: MatchLineScoreDraft): string {
  return compactSetScores(line).join(' ') || 'No score';
}

function specialSuffix(line: Pick<MatchLineScoreDraft, 'status' | 'retired' | 'defaulted' | 'walkover'>): string {
  if (line.status === 'Retired' || line.retired) return ' (retired)';
  if (line.status === 'Double Default') return ' (double default)';
  if (line.status === 'Default') return ' (default)';
  if (line.walkover) return ' (walkover)';
  if (line.defaulted) return ' (default)';
  return '';
}
